import { takeEvery, put, select, getContext, call } from "redux-saga/effects";

import * as actionTypes from "constants/actionTypes";
import * as accountManagementActionTypes from "constants/accountManagement/actionTypes";
import { selectAccountUsers } from "reducers/account/users";
import { selectUser } from "reducers/user";
import { showToast } from "actions/toaster";
import {
  getAccountAdminUserIds,
  buildUserPermissionProperties,
  canManageAccountUsers,
  getPermissionValueForUser
} from "helpers/account-management";
import { reportError } from "helpers/error-reporting";
import { removeUser } from "actions/account/users";

export function* removeAccountUserAndFlushUsers(action) {
  const { userToRemove } = action.payload;

  const api = yield getContext("accounts");

  const accountUsers = yield select(selectAccountUsers);
  const loggedInUser = yield select(selectUser);
  const adminUserIds = getAccountAdminUserIds(accountUsers);

  if (!canManageAccountUsers({ user: loggedInUser, accountUsers })) {
    console.error(
      `(loggedInuser=${loggedInUser.id}, remove=${userToRemove.id}): is not account admin`
    );

    throw new Error("Cannot remove user if you are not an admin.");
  }

  if (adminUserIds.has(userToRemove.id) && adminUserIds.size < 2) {
    console.error(
      `(loggedInuser=${loggedInUser.id}, remove=${userToRemove.id}): cannot remove last admin`
    );
    throw new Error("Cannot remove the last admin from the team.");
  }

  try {
    yield call(api.removeUser, { uid: userToRemove.id });

    yield put(removeUser({ id: userToRemove.id }));

    yield put({
      type: actionTypes.TRACK_EVENT,
      payload: {
        eventName: "Removed User From Team",
        properties: {
          userId: userToRemove.id,
          email: userToRemove.email
        }
      }
    });
  } catch (e) {
    yield call(reportError, e);
    yield put(
      showToast({
        message: "Failed to remove user from account",
        type: "error"
      })
    );
  }
}

export function* updateAccountUserPermissionsAndFlushUsers(action) {
  const { userToUpdate, permissionOption } = action.payload;
  const accountUsers = yield select(selectAccountUsers);
  const loggedInUser = yield select(selectUser);

  if (!canManageAccountUsers({ user: loggedInUser, accountUsers })) {
    console.error(
      `(loggedInuser=${loggedInUser.id}, remove=${userToUpdate.id}): is not account admin`
    );
  }

  if (userToUpdate.id === loggedInUser.id) {
    console.error(
      `(loggedInuser=${loggedInUser.id}, remove=${userToUpdate.id}): you can't update your own permission level`
    );
  }

  const newUserPermissions = buildUserPermissionProperties(permissionOption);

  const userWithUpdatedPermissions = Object.assign(
    {},
    accountUsers[userToUpdate.id],
    newUserPermissions
  );

  const updatedAccountUsers = Object.keys(accountUsers).reduce(
    (updatedAccountUsersObject, accountUserIdKey) => {
      if (accountUserIdKey === userToUpdate.id) {
        return Object.assign({}, updatedAccountUsersObject, {
          [accountUserIdKey]: userWithUpdatedPermissions
        });
      }
      return Object.assign({}, updatedAccountUsersObject, {
        [accountUserIdKey]: accountUsers[accountUserIdKey]
      });
    },
    {}
  );

  try {
    yield put({
      type: actionTypes.REPLACE_ACCOUNT_USERS,
      payload: updatedAccountUsers
    });

    yield put({
      type: actionTypes.FLUSH_ACCOUNT_USER_TO_FIREBASE,
      payload: { userId: userToUpdate.id }
    });

    yield put({
      type: actionTypes.TRACK_EVENT,
      payload: {
        eventName: "Changed User Permission",
        properties: {
          userId: userToUpdate.id,
          newPermission: permissionOption.value,
          oldPermission: getPermissionValueForUser({
            user: accountUsers[userToUpdate.id],
            accountUsers
          })
        }
      }
    });
    yield put(
      showToast({
        message: "Role updated",
        type: "success"
      })
    );
  } catch (err) {
    yield call(reportError, err);
  }
}

export default function* accountManagement() {
  yield takeEvery(
    accountManagementActionTypes.REMOVE_ACCOUNT_USER_AND_FLUSH_USERS,
    removeAccountUserAndFlushUsers
  );

  yield takeEvery(
    accountManagementActionTypes.UPDATE_ACCOUNT_USER_PERMISSIONS_AND_FLUSH_USERS,
    updateAccountUserPermissionsAndFlushUsers
  );
}
